import { ReactElement, useEffect, useState } from 'react'

import { useSelector } from 'react-redux'

import { LoadingIndicator } from 'components/LoadingIndicator'
import { DynamicModule } from 'dynamicModules/types'
import { getAllAsyncReducerKeys } from 'reducers/ui/ui'
import { PageId } from 'routing/pages'
import { selectDomainValue as selectUiDomainValue } from 'selectors/ui'
import { RootReduxState } from 'types'

export type DynamicPageProps = {
    pageName: PageId
}

// Track which modules have been initialized already
const initCache: Record<string, boolean> = {}
const hydrateCache: Record<string, boolean> = {}

function useDynamicModuleInit(
    pageName: PageId,
    module: DynamicModule
): boolean {
    const [isInitialized, setIsInitialized] = useState(
        initCache[pageName] ?? false
    )
    const [isHydrated, setIsHydrated] = useState(
        hydrateCache[pageName] ?? false
    )

    const requiresDynamicReducer = (
        getAllAsyncReducerKeys() as PageId[]
    ).includes(pageName)
    const isReducerHydrated = useSelector((state: RootReduxState) =>
        selectUiDomainValue(state, [pageName, 'hydrated'])
    )
    useEffect(() => {
        if (!initCache[pageName]) {
            module.init()
            setIsInitialized(true)
            initCache[pageName] = true
        }
    }, [pageName, module])

    useEffect(() => {
        if (isReducerHydrated) {
            setIsHydrated(true)
            hydrateCache[pageName] = true
        }
    }, [pageName, isReducerHydrated, module])

    return isInitialized && (!requiresDynamicReducer || isHydrated)
}

function DynamicModuleInitializer({
    pageName,
    module,
    children,
}: {
    pageName: PageId
    module: DynamicModule
    children: ReactElement
}): ReactElement {
    const isInitialized = useDynamicModuleInit(pageName, module)

    return isInitialized ? children : <LoadingIndicator size="small" />
}

export default function DynamicPageRoute(module: DynamicModule) {
    // eslint-disable-next-line react/display-name
    return ({ pageName, ...props }: DynamicPageProps) => {
        return (
            <DynamicModuleInitializer module={module} pageName={pageName}>
                <module.page {...(props as any)} />
            </DynamicModuleInitializer>
        )
    }
}
